const SYSEX_START = 0xf0;
const SYSEX_END = 0xf7;
const MANUFACTURER_ID = [0x7d, 0x42, 0x42];
const MSG_SETTINGS_REQUEST = 0x10;
const MSG_SETTINGS_DUMP = 0x11;
const MSG_SETTINGS_UPDATE = 0x12;

const ROUTING_BIT_LABELS = ['Bus 1', 'Bus 2', 'Bus 3', 'Bus 4'];
const NOTE_MUTES_CHANNEL = 70;
const NOTE_SOLOS_CHANNEL = 71;
const NOTE_PROGRAM_CHANNEL = 72;
const GROUP_TITLES = {
  drum: 'Drum Notes',
  midi: 'MIDI Channels',
  routing: 'Routing',
  toggles: 'Options & Safety',
};

const SETTINGS_ORDER = [
  30,
  31,
  32,
  33,
  34,
  35,
  9,
  10,
  11,
  12,
  13,
  NOTE_MUTES_CHANNEL,
  NOTE_SOLOS_CHANNEL,
  NOTE_PROGRAM_CHANNEL,
  3,
  4,
  5,
  6,
  7,
  18,
  19,
  8,
  15,
  29,
  23,
  24,
  25,
  26,
];

const SETTINGS_DEFINITIONS = {
  30: { label: 'Kick Note', type: 'note', group: 'drum' },
  31: { label: 'Snare Note', type: 'note', group: 'drum' },
  32: { label: 'Tom Note', type: 'note', group: 'drum' },
  33: { label: 'Closed Hat Note', type: 'note', group: 'drum' },
  34: { label: 'Open Hat Note', type: 'note', group: 'drum' },
  35: { label: 'Cymbal Note', type: 'note', group: 'drum' },

  9: { label: 'Drum MIDI Channel', type: 'channel', group: 'midi' },
  10: { label: 'Synth 1 MIDI Channel', type: 'channel', group: 'midi' },
  11: { label: 'Synth 2 MIDI Channel', type: 'channel', group: 'midi' },
  12: { label: 'Synth 3 MIDI Channel', type: 'channel', group: 'midi' },
  13: { label: 'Synth 4 MIDI Channel', type: 'channel', group: 'midi' },
  [NOTE_MUTES_CHANNEL]: { label: 'Mutes Channel', type: 'channel', group: 'midi' },
  [NOTE_SOLOS_CHANNEL]: { label: 'Solos Channel', type: 'channel', group: 'midi' },
  [NOTE_PROGRAM_CHANNEL]: { label: 'Program Change Channel', type: 'channel', group: 'midi' },

  3: { label: 'Kick Output Routing', type: 'routing', bits: ROUTING_BIT_LABELS, group: 'routing' },
  4: { label: 'Snare & Hats Routing', type: 'routing', bits: ROUTING_BIT_LABELS, group: 'routing' },
  5: { label: 'Tom & Cym Routing', type: 'routing', bits: ROUTING_BIT_LABELS, group: 'routing' },
  6: { label: 'Synth 1 Routing', type: 'routing', bits: ROUTING_BIT_LABELS, group: 'routing' },
  7: { label: 'Synth 2 Routing', type: 'routing', bits: ROUTING_BIT_LABELS, group: 'routing' },
  18: { label: 'Synth 3 Routing', type: 'routing', bits: ROUTING_BIT_LABELS, group: 'routing' },
  19: { label: 'Synth 4 Routing', type: 'routing', bits: ROUTING_BIT_LABELS, group: 'routing' },

  8: { label: 'Safe Envelope Ranges', type: 'boolean', group: 'toggles' },
  15: { label: 'Fast Pitch Fall', type: 'boolean', group: 'toggles' },
  29: { label: 'Quantise', type: 'boolean', group: 'toggles' },
  23: { label: 'Metronome', type: 'boolean', group: 'toggles' },
  24: { label: 'Save Kit with Pattern', type: 'boolean', group: 'toggles' },
  25: { label: 'Wiggle Only Active Step', type: 'boolean', group: 'toggles' },
  26: { label: 'Ignore Incoming Notes', type: 'boolean', group: 'toggles' },
};

const dynamicDefinitions = new Map();
const settingsState = {};
const settingUI = new Map();

let activeInput = null;
let activeOutput = null;
let handshakeInFlight = false;
let midiBridge = null;
let detachConnectionListener = null;
let detachMessageListener = null;
let lastConnectionIds = { input: null, output: null };

let statusText;
let settingsContainer;

const MIDI_NOTES = (() => {
  const list = [];
  for (let n = 0; n < 128; n += 1) {
    list.push({ value: n, label: `${midiNoteName(n)} (${n})` });
  }
  return list;
})();

function setup() {
  noCanvas();

  statusText = select('#status-indicator');
  settingsContainer = select('#settings');

  initialiseSettingRows();
  updateControlsAvailability();

  connectToPatternMidi();
}

function connectToPatternMidi() {
  const bridge = window.blastBeatsMidi;
  if (bridge && typeof bridge.onConnectionChange === 'function') {
    attachToBridge(bridge);
  } else {
    setStatus('Waiting for Blast Beats USB…');
    window.addEventListener('blastbeats-midi-ready', handleBridgeReady, { once: true });
  }
}

function handleBridgeReady(event) {
  const bridge = event?.detail || window.blastBeatsMidi;
  attachToBridge(bridge);
}

function attachToBridge(bridge) {
  if (!bridge) return;
  midiBridge = bridge;

  if (detachConnectionListener) {
    detachConnectionListener();
    detachConnectionListener = null;
  }
  if (detachMessageListener) {
    detachMessageListener();
    detachMessageListener = null;
  }

  if (typeof bridge.onConnectionChange === 'function') {
    detachConnectionListener = bridge.onConnectionChange(handleConnectionChange);
  }
  if (typeof bridge.onMessage === 'function') {
    detachMessageListener = bridge.onMessage(handleSettingsMidiMessage);
  }

  if (typeof bridge.getCurrentConnection === 'function') {
    handleConnectionChange(bridge.getCurrentConnection());
  } else {
    handleConnectionChange({ input: null, output: null });
  }
}

function initialiseSettingRows() {
  settingsContainer.html('');
  const seenGroups = new Set();

  SETTINGS_ORDER.forEach((note) => {
    const def = SETTINGS_DEFINITIONS[note];
    if (!def) return;
    insertGroupIfNeeded(def.group, seenGroups);
    ensureSettingRow(note, def);
  });

  // Append any remaining known settings not explicitly ordered
  Object.keys(SETTINGS_DEFINITIONS)
    .map(Number)
    .filter((note) => !SETTINGS_ORDER.includes(note))
    .forEach((note) => {
      const def = SETTINGS_DEFINITIONS[note];
      insertGroupIfNeeded(def.group, seenGroups);
      ensureSettingRow(note, def);
    });
}

function insertGroupIfNeeded(group, seenGroups) {
  if (!group || seenGroups.has(group)) return;
  const heading = createDiv(GROUP_TITLES[group] || group);
  heading.addClass('group-heading');
  heading.parent(settingsContainer);
  seenGroups.add(group);
}

function updateControlsAvailability() {
  // Manual controls removed; nothing to toggle.
}

function handleConnectionChange(connection) {
  const input = connection?.input || null;
  const output = connection?.output || null;

  const inputId = input ? input.id || input.name || 'input' : null;
  const outputId = output ? output.id || output.name || 'output' : null;
  const connectionChanged =
    inputId !== lastConnectionIds.input || outputId !== lastConnectionIds.output;

  lastConnectionIds = { input: inputId, output: outputId };

  activeInput = input;
  activeOutput = output;

  updateControlsAvailability();

  if (activeInput && activeOutput) {
    if (connectionChanged) {
      sendHandshake(true);
    } else if (!handshakeInFlight) {
      setStatus(`Connected to ${output?.name || 'BlastBeats MIDI'}.`);
    }
  } else {
    handshakeInFlight = false;
    setStatus('Connect Blast Beats via USB to sync settings.');
  }
}

function sendHandshake(isAuto = false) {
  if (!activeOutput || !activeInput) {
    updateControlsAvailability();
    setStatus('Connect Blast Beats via USB before reading settings.', true);
    return false;
  }

  const packet = [
    SYSEX_START,
    ...MANUFACTURER_ID,
    MSG_SETTINGS_REQUEST,
    SYSEX_END,
  ];
  activeOutput.send(packet);

  handshakeInFlight = true;
  setStatus(
    isAuto
      ? 'Connected. Requesting current settings…'
      : 'Request sent. Waiting for the device to respond with its settings…'
  );
  return true;
}

function sendAllSettings() {
  if (!activeOutput || !activeInput) {
    setStatus('Connect Blast Beats via USB before sending settings.', true);
    return;
  }

  const entries = Object.entries(settingsState);
  if (entries.length === 0) {
    setStatus('No settings cached yet. Read from the device first.', true);
    return;
  }

  const payload = [];
  entries.forEach(([note, rawValue]) => {
    const numericNote = Number(note);
    if (Number.isNaN(numericNote)) return;
    const def =
      SETTINGS_DEFINITIONS[numericNote] || dynamicDefinitions.get(numericNote);
    if (def && def.writable === false) return;
    payload.push(numericNote & 0x7f, rawValue & 0x7f);
  });

  if (payload.length === 0) {
    setStatus('No writable settings to send.', true);
    return;
  }

  const pairCount = payload.length / 2;
  const packet = [
    SYSEX_START,
    ...MANUFACTURER_ID,
    MSG_SETTINGS_UPDATE,
    pairCount & 0x7f,
    ...payload,
    SYSEX_END,
  ];
  activeOutput.send(packet);

  setStatus('Sent current settings to the device.');
}

function handleSettingsMidiMessage(event) {
  const data = event?.data;
  if (!data || data.length < 6) return;
  if (data[0] !== SYSEX_START || data[data.length - 1] !== SYSEX_END) return;
  if (
    data[1] !== MANUFACTURER_ID[0] ||
    data[2] !== MANUFACTURER_ID[1] ||
    data[3] !== MANUFACTURER_ID[2]
  ) {
    return;
  }

  const type = data[4];
  switch (type) {
    case MSG_SETTINGS_DUMP:
    case MSG_SETTINGS_UPDATE: {
      const requestedPairs = data[5] & 0x7f;
      let index = 6;
      const availablePairs = Math.min(
        requestedPairs,
        Math.floor((data.length - 1 - index) / 2)
      );

      for (let i = 0; i < availablePairs; i++) {
        const note = data[index++] & 0x7f;
        const rawValue = data[index++] & 0x7f;
        applySetting(note, rawValue);
      }

      if (type === MSG_SETTINGS_DUMP) {
        handshakeInFlight = false;
        setStatus('Settings synced from the device.');
      }
      break;
    }
    default:
      break;
  }
}

function applySetting(note, rawValue) {
  if (note === 0 || note === 1) {
    return;
  }

  let def = SETTINGS_DEFINITIONS[note];
  if (!def) {
    if (!dynamicDefinitions.has(note)) {
      dynamicDefinitions.set(note, {
        label: `Note ${note}`,
        type: 'raw',
        writable: false,
      });
    }
    def = dynamicDefinitions.get(note);
  }

  settingsState[note] = rawValue;
  updateSettingRow(note, def, rawValue);

  if (handshakeInFlight) {
    handshakeInFlight = false;
    setStatus('Settings received from the device.');
  }
}

function ensureSettingRow(note, def) {
  if (settingUI.has(note)) return settingUI.get(note);

  const row = createDiv();
  row.addClass('row');
  row.parent(settingsContainer);

  const labelText = def.label || '';
  const label = createSpan(labelText);
  label.addClass('label');
  label.parent(row);

  const valueSpan = createSpan('—');
  valueSpan.addClass('value');
  valueSpan.parent(row);

  let control = null;
  if (def.writable === false) {
    const placeholder = createSpan('—');
    placeholder.addClass('small');
    placeholder.parent(row);
  } else {
    control = createControlForDef(note, def, row);
  }

  const uiEntry = { row, valueSpan, control, def };
  settingUI.set(note, uiEntry);
  return uiEntry;
}

function createControlForDef(note, def, parent) {
  if (def.type === 'boolean') {
    const checkbox = createElement('input');
    checkbox.attribute('type', 'checkbox');
    checkbox.parent(parent);
    checkbox.addClass('toggle');
    checkbox.elt.addEventListener('change', () => {
      sendSetting(note, checkbox.elt.checked);
    });
    return {
      element: checkbox,
      setValue: (val) => {
        checkbox.elt.checked = !!val;
      },
    };
  }

  if (def.type === 'channel') {
    const selectEl = createSelect();
    for (let i = 1; i <= 16; i += 1) {
      selectEl.option(`Ch ${i}`, String(i));
    }
    selectEl.parent(parent);
    selectEl.changed(() => {
      const value = parseInt(selectEl.value(), 10);
      if (!Number.isNaN(value)) {
        sendSetting(note, value);
      }
    });
    return {
      element: selectEl,
      setValue: (val) => {
        selectEl.value(String(val));
      },
    };
  }

  if (def.type === 'note') {
    const selectEl = createSelect();
    MIDI_NOTES.forEach((option) => {
      selectEl.option(option.label, String(option.value));
    });
    selectEl.parent(parent);
    selectEl.changed(() => {
      const value = parseInt(selectEl.value(), 10);
      if (!Number.isNaN(value)) {
        sendSetting(note, value);
      }
    });
    return {
      element: selectEl,
      setValue: (val) => {
        if (!selectEl.elt.querySelector(`option[value="${val}"]`)) {
          selectEl.option(`${val}`, String(val));
        }
        selectEl.value(String(val));
      },
    };
  }

  if (def.type === 'routing') {
    const grid = createDiv();
    grid.addClass('routing-grid');
    grid.parent(parent);

    const bitControls = [];
    let isUpdating = false;

    for (let index = 0; index < 4; index += 1) {
      const checkbox = createCheckbox('', false);
      checkbox.addClass('routing-checkbox');
      checkbox.parent(grid);
      checkbox.changed(() => {
        if (isUpdating) return;
        const value = bitControls.reduce((acc, cb, idx) => {
          return acc | (cb.checked() ? (1 << idx) : 0);
        }, 0);
        sendSetting(note, value);
      });
      bitControls.push(checkbox);
    }

    return {
      element: grid,
      setValue: (val) => {
        isUpdating = true;
        bitControls.forEach((cb, idx) => {
          cb.checked(Boolean(val & (1 << idx)));
        });
        isUpdating = false;
      },
    };
  }

  const input = createInput('', 'number');
  input.attribute('min', Number.isFinite(def.min) ? def.min : 0);
  input.attribute('max', Number.isFinite(def.max) ? def.max : 127);
  input.attribute('step', 1);
  input.parent(parent);

  input.changed(() => {
    const raw = parseInt(input.value(), 10);
    if (!Number.isNaN(raw)) {
      const min = Number.isFinite(def.min) ? def.min : 0;
      const max = Number.isFinite(def.max) ? def.max : 127;
      const clamped = clamp(raw, min, max);
      if (clamped !== raw) input.value(clamped);
      sendSetting(note, clamped);
    }
  });

  return {
    element: input,
    setValue: (val) => {
      input.value(val);
    },
  };
}

function updateSettingRow(note, def, rawValue) {
  const uiEntry = ensureSettingRow(note, def);

  const decoded = decodeValue(def, rawValue);
  const humanReadable = formatValue(def, decoded, rawValue);

  const displayValue =
    def.group === 'toggles' && def.type === 'boolean' ? '' : humanReadable;

  uiEntry.valueSpan.html(displayValue);
  if (uiEntry.control && typeof uiEntry.control.setValue === 'function') {
    uiEntry.control.setValue(decoded);
  }
}

function sendSetting(note, uiValue) {
  const def = SETTINGS_DEFINITIONS[note] || dynamicDefinitions.get(note);
  if (!def || def.writable === false) return;

  if (!activeOutput) {
    setStatus('Connect Blast Beats via USB before sending settings.', true);
    return;
  }

  const rawValue = encodeValue(def, uiValue);
  const packet = [
    SYSEX_START,
    ...MANUFACTURER_ID,
    MSG_SETTINGS_UPDATE,
    0x01,
    note & 0x7f,
    rawValue & 0x7f,
    SYSEX_END,
  ];
  activeOutput.send(packet);

  settingsState[note] = rawValue;
  updateSettingRow(note, def, rawValue);

  const decoded = decodeValue(def, rawValue);
  setStatus(`Sent ${def.label || `Note ${note}`} → ${formatValue(def, decoded, rawValue)}.`);
}

function decodeValue(def, raw) {
  switch (def.type) {
    case 'boolean':
      return raw > 0;
    case 'channel':
      return clamp(raw + 1, 1, 16);
    case 'routing':
      return clamp(raw, 0, 15);
    default:
      return raw;
  }
}

function encodeValue(def, value) {
  switch (def.type) {
    case 'boolean':
      return value ? 1 : 0;
    case 'channel': {
      const channel = clamp(Math.round(value), 1, 16);
      return channel - 1;
    }
    case 'routing':
      return clamp(Math.round(value), 0, 15);
    default: {
      const min = Number.isFinite(def.min) ? def.min : 0;
      const max = Number.isFinite(def.max) ? def.max : 127;
      return clamp(Math.round(value), min, max);
    }
  }
}

function formatValue(def, decoded, raw) {
  switch (def.type) {
    case 'boolean':
      return decoded ? 'On' : 'Off';
    case 'channel':
      return `Ch ${decoded}`;
    case 'note': {
      const name = midiNoteName(decoded);
      return name ? `${name} (${decoded})` : decoded;
    }
    case 'routing': {
      return '';
    }
    default:
      return decoded;
  }
}

function midiNoteName(number) {
  if (Number.isNaN(number) || number < 0 || number > 127) return '';
  const baseNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const name = baseNames[number % 12];
  const octave = Math.floor(number / 12) - 1;
  return `${name}${octave}`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function setStatus(message, isError = false) {
  statusText.html(message);
  statusText.style('color', isError ? '#f97316' : '#a5b4fc');
}
